import _ from "lodash";
/** external */
import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  SearchInput,
  FilterLabelButton,
  ChecklistDropdown,
  IconDisclosure,
  SegmentControl,
  Dropdown,
  DropdownMenu,
  Tooltip,
} from "@toddle-edu/ds-web";
import { FilterOutlined } from "@toddle-edu/ds-icons";
/** internal */
import { useI18n } from "Hooks";
import { getLabelValueForMultiFilterLabelButton } from "Utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import {
  QUESTION_TYPE_DETAILS,
  TODDLE_LIBRARY,
} from "Worksheet/modules/WorksheetConstants";
import {
  updateQuestionLibraryFilters,
  updateQuestionLibraryType,
  resetQuestionLibraryState,
} from "Worksheet/modules/WorksheetModules";
import { TODDLE_LIBRARY as ACTIVE_TAB_TODDLE_LIBRARY } from "Constants/stringConstants";
import {
  getLibraryTypeOptions,
  getQuestionOrderOptions,
  getQuestionFeedTypeOptions,
  isToddleLibraryEnabledForWorksheets,
  getDifficultyLevelOptions,
} from "Worksheet/modules/WorksheetUtils";
import ACLStore from "lib/aclStore";
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";
import { getGlobalGradeUid } from "LearningCourses/modules/Utils";
/** relative */
import classes from "./QuestionLibraryFilters.scss";

const styles = {
  labelStyle: {
    maxWidth: "140px",
  },
  dropdownStyle: {
    maxWidth: "200px",
  },
};

const QuestionLibraryFilters = props => {
  const {
    filters,
    totalCount,
    updateQuestionLibraryFilters,
    gradeOptions,
    subjectOptions,
    staffOptions,
    topics,
    subTopics,
    isSubQuestion,
    updateQuestionLibraryType,
    libraryType,
    resetQuestionLibraryState,
    setShouldApplyDefaultFilters,
    isToddleLibraryPortal,
    curriculumType,
    globalGradeUid,
    paperTypes,
    setPreviewId,
    setPreviewResourceId,
  } = props;

  const { t } = useI18n(["common"]);
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const isToddleLibrary = _.isEqual(libraryType, TODDLE_LIBRARY);
  const showLibraryTypeSwitcher = isToddleLibraryEnabledForWorksheets({
    curriculumType,
    globalGradeUid,
  });

  const {
    searchText = "",
    selectedGrades = [],
    selectedSubjects = [],
    selectedStaff = [],
    selectedDifficulty = [],
    selectedQuestionTypes = [],
    selectedTopics = [],
    orderBy = "",
    questionFeedType = "",
    selectedSubTopics = [],
    selectedPaperTypes = [],
  } = filters || {};

  const topicOptions = _.map(topics, topic => {
    return {
      label: _.get(topic, "label"),
      value: _.get(topic, "id"),
    };
  });
  const subTopicOptions = useMemo(() => {
    const withParentSequence = _.map(subTopics, subTopic => {
      const parentTopic = _.find(topics, { id: subTopic.parent });
      return {
        ...subTopic,
        parentDisplaySequence: _.get(parentTopic, "displaySequence", 0),
      };
    });
    return _.map(
      _.sortBy(withParentSequence, [
        "parentDisplaySequence",
        "displaySequence",
      ]),
      subTopic => ({
        label: _.get(subTopic, "label"),
        value: _.get(subTopic, "id"),
      })
    );
  }, [subTopics, topics]);

  const questionFeedTypeOptions = getQuestionFeedTypeOptions(t);
  const difficultyOptions = getDifficultyLevelOptions(t);
  const questionTypeOptions = _.map(
    _.filter(QUESTION_TYPE_DETAILS, questionType => {
      if (!ACLStore.can(_.get(questionType, "perm"))) return false;

      if (isSubQuestion) {
        return (
          _.get(questionType, "canAddToLibrary", false) &&
          _.get(questionType, "isSubQuestion", false)
        );
      } else {
        return _.get(questionType, "canAddToLibrary", false);
      }
    }),
    params => ({
      label: t(_.get(params, "localeLabel", "")),
      value: _.get(params, "key", ""),
    })
  );

  // Transform paper types array into options format required by dropdown
  const paperTypesOptions = _.map(paperTypes, topic => {
    return {
      label: _.get(topic, "label"),
      value: _.get(topic, "id"),
    };
  });

  const questionOrderOptions = getQuestionOrderOptions(t);

  /**
   * @returns subject options for diff curriculum type
   * for "IB_MYP", "IB_DP" it will be grouped by subject group
   * if total valid group ids is not equal to total subjects, it will be simply alpha order by label
   */
  const subjectOptionsWithAllOption = useMemo(() => {
    let _subjectOptions = subjectOptions;
    const subjectGroupIDs = _.compact(_.map(subjectOptions, "subjectGroup.id"));
    if (
      _.includes(["IB_MYP", "IB_DP"], curriculumType) &&
      _.size(subjectOptions) === _.size(subjectGroupIDs)
    ) {
      // * MYP and DP curriculum, showing subject group wise
      const groupedSubject = [];
      _.forEach(subjectOptions, subject => {
        if (_.get(subject, "subjectGroup")) {
          const subjectGroupKey = _.get(subject, "subjectGroup.id");
          const groupedItem = _.find(groupedSubject, { key: subjectGroupKey });
          const subjectObject = _.pick(subject, ["key", "label"]);
          if (groupedItem) {
            groupedItem.options.push(subjectObject);
          } else {
            groupedSubject.push({
              isItemGroup: true,
              key: subjectGroupKey,
              options: [subjectObject],
              label: _.get(subject, "subjectGroup.name"),
            });
          }
        }
      });
      _subjectOptions = groupedSubject;
    }

    return [
      { key: "all", label: t("academicSetup:all_subjects") },
      ..._.orderBy(_subjectOptions, "label"),
    ];
  }, [isToddleLibraryPortal, subjectOptions, curriculumType]);

  const toggleMoreFilters = () => {
    setShowMoreFilters(prev => !prev);
  };

  const updateLibraryType = params => {
    /** clear selected filters while switching library types*/
    resetQuestionLibraryState();

    /** set the default filters of the library type */
    setShouldApplyDefaultFilters(true);

    /** clear question that is currently shown in the right pane */
    //WORKSHEET_RESTRUCTURING:Remove setPreviewResourceId once support is added in internal tool and toddle library
    setPreviewResourceId(null);
    setPreviewId(null);

    updateQuestionLibraryType(params);
  };
  const updateQuestionOrder = ({ key }) => {
    updateQuestionLibraryFilters({
      key: "orderBy",
      value: key,
    });
  };

  const updateTopicFilter = params => {
    updateQuestionLibraryFilters({
      key: "selectedTopics",
      value: params,
    });
  };

  const updateSubTopicFilter = params => {
    updateQuestionLibraryFilters({
      key: "selectedSubTopics",
      value: params,
    });
  };

  const updateSubjectFilter = ({ key }) => {
    updateQuestionLibraryFilters({
      key: "selectedSubjects",
      value: _.isEqual(key, "all") ? [] : [key],
    });
  };

  const updateQuestionFeedFilter = ({ key }) => {
    updateQuestionLibraryFilters({
      key: "questionFeedType",
      value: key,
    });
  };

  const disablePlannerElementsDropdown =
    libraryType === TODDLE_LIBRARY
      ? !_.isEqual(_.size(selectedSubjects), 1)
      : false;

  const selectedSubjectsValue = _.isEqual(_.size(selectedSubjects), 1)
    ? _.first(selectedSubjects)
    : "all";

  const selectedSubjectLabel = useMemo(() => {
    const subjectItem = _.find(subjectOptionsWithAllOption, opt => {
      if (_.isEqual(_.get(opt, "key"), selectedSubjectsValue)) return true;
      if (_.get(opt, "isItemGroup")) {
        return _.some(_.get(opt, "options", []), {
          key: selectedSubjectsValue,
        });
      }
      return false;
    });

    if (!subjectItem) return "";

    const resolvedItem = _.get(subjectItem, "isItemGroup")
      ? _.find(_.get(subjectItem, "options", []), {
          key: selectedSubjectsValue,
        })
      : subjectItem;

    return _.get(resolvedItem, "label", "");
  }, [subjectOptionsWithAllOption, selectedSubjectsValue]);

  return (
    <div className={classes.container}>
      {/* Search and switcher */}
      <div className={classes.searchContainer}>
        <div className={classes.searchInput}>
          <SearchInput
            size="medium"
            placeholder={t("common:search_for_with_label", {
              label: "topics or questions",
            })}
            value={searchText}
            onChange={params => {
              updateQuestionLibraryFilters({
                key: "searchText",
                value: params,
              });
            }}
          />
        </div>
        {showLibraryTypeSwitcher ? (
          <SegmentControl
            name="library-type-switcher"
            onChange={updateLibraryType}
            options={getLibraryTypeOptions({ t })}
            size="medium"
            value={libraryType}
            variant="text"
            testId="question-library-type-switcher"
          />
        ) : null}
      </div>
      {/* Count and filters */}
      <div className={classes.filterContainer}>
        <div className={"heading-6"}>
          {t("common:question_number", {
            count: totalCount,
          })}
        </div>
        <div className={classes.filters}>
          {/* Sort by */}
          {!isToddleLibrary ? (
            <Dropdown
              overlay={
                <DropdownMenu
                  onClick={updateQuestionOrder}
                  options={questionOrderOptions}
                  showSelection
                  value={orderBy}
                  itemTitleContainerStyle={styles.dropdownStyle}
                />
              }
            >
              <FilterLabelButton
                dsVersion="2.0"
                _legacy_variant="plain"
                variant="neutral"
                type="plain"
                label={t("common:sort_by")}
                labelValue={_.get(
                  _.find(questionOrderOptions, { key: orderBy }),
                  "label",
                  ""
                )}
                labelStyle={styles.labelStyle}
              />
            </Dropdown>
          ) : null}
          {isToddleLibrary ? (
            <Dropdown
              overlay={
                <DropdownMenu
                  options={questionFeedTypeOptions}
                  onClick={updateQuestionFeedFilter}
                />
              }
            >
              <FilterLabelButton
                dsVersion="2.0"
                variant="neutral"
                type="plain"
                _legacy_variant="plain"
                labelValue={_.get(
                  _.find(questionFeedTypeOptions, questionFeed => {
                    return _.isEqual(
                      _.get(questionFeed, "key"),
                      questionFeedType
                    );
                  }),
                  "label",
                  ""
                )}
              />
            </Dropdown>
          ) : null}
          {/* Subject */}
          <Dropdown
            overlay={
              <DropdownMenu
                onClick={updateSubjectFilter}
                options={subjectOptionsWithAllOption}
                showSelection
                selectionType="tick"
                value={selectedSubjectsValue}
                isSearchEnabled={true}
                itemTitleContainerStyle={styles.dropdownStyle}
                style={{ maxHeight: "50vh", overflowY: "auto" }}
              />
            }
          >
            <FilterLabelButton
              dsVersion="2.0"
              variant="neutral"
              type="plain"
              _legacy_variant="plain"
              label={t("common:subject")}
              labelValue={selectedSubjectLabel}
              labelStyle={styles.labelStyle}
            />
          </Dropdown>
          {/* Grade */}
          {!isToddleLibrary ? (
            <ChecklistDropdown
              options={gradeOptions}
              value={selectedGrades}
              onChange={params => {
                updateQuestionLibraryFilters({
                  key: "selectedGrades",
                  value: params,
                });
              }}
              showAllOption={true}
              showSelectedOptionsOnTop={false}
              isSearchEnabled={true}
            >
              <FilterLabelButton
                dsVersion="2.0"
                _legacy_variant="plain"
                variant="neutral"
                type="plain"
                label={t("common:grade")}
                labelValue={getLabelValueForMultiFilterLabelButton({
                  options: gradeOptions,
                  selectedOptions: selectedGrades,
                  allText: t("common:all"),
                  noneText: t("common:none"),
                })}
              />
            </ChecklistDropdown>
          ) : null}
          {/* More filters toggle */}
          <IconDisclosure
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            icon={<FilterOutlined />}
            _legacy_variant="outlined-subtle"
            onClick={toggleMoreFilters}
            isActivated={showMoreFilters}
          />
        </div>
      </div>
      {/* more filters */}
      {showMoreFilters ? (
        <div className={classes.moreFilters}>
          {/* Author */}
          {!isToddleLibrary ? (
            <ChecklistDropdown
              options={staffOptions}
              value={selectedStaff}
              onChange={params => {
                updateQuestionLibraryFilters({
                  key: "selectedStaff",
                  value: params,
                });
              }}
              showAllOption={true}
              showSelectedOptionsOnTop={false}
              isSearchEnabled={true}
            >
              <FilterLabelButton
                dsVersion="2.0"
                _legacy_variant="plain"
                variant="neutral"
                type="plain"
                label={t("common:created_by")}
                labelValue={getLabelValueForMultiFilterLabelButton({
                  options: staffOptions,
                  selectedOptions: selectedStaff,
                  allText: t("common:all"),
                  noneText: t("common:all"),
                })}
              />
            </ChecklistDropdown>
          ) : null}
          {/* Paper type */}
          {isToddleLibrary ? (
            <ChecklistDropdown
              options={paperTypesOptions}
              value={selectedPaperTypes}
              onChange={params => {
                updateQuestionLibraryFilters({
                  key: "selectedPaperTypes",
                  value: params,
                });
              }}
              showAllOption={true}
              showSelectedOptionsOnTop={false}
            >
              <FilterLabelButton
                variant="plain"
                label={t("worksheet:paper_type")}
                labelValue={getLabelValueForMultiFilterLabelButton({
                  options: paperTypesOptions,
                  selectedOptions: selectedPaperTypes,
                  allText: t("common:all"),
                  noneText: t("common:all"),
                })}
              />
            </ChecklistDropdown>
          ) : null}
          {/* Difficulty */}
          <ChecklistDropdown
            options={difficultyOptions}
            value={selectedDifficulty}
            onChange={params => {
              updateQuestionLibraryFilters({
                key: "selectedDifficulty",
                value: params,
              });
            }}
            showAllOption={true}
            showSelectedOptionsOnTop={false}
          >
            <FilterLabelButton
              dsVersion="2.0"
              _legacy_variant="plain"
              variant="neutral"
              type="plain"
              label={t("worksheet:difficulty")}
              labelValue={getLabelValueForMultiFilterLabelButton({
                options: difficultyOptions,
                selectedOptions: selectedDifficulty,
                allText: t("common:all"),
                noneText: t("common:all"),
              })}
            />
          </ChecklistDropdown>
          {/* Question type */}
          {!isToddleLibrary ? (
            <ChecklistDropdown
              options={questionTypeOptions}
              value={selectedQuestionTypes}
              onChange={params => {
                updateQuestionLibraryFilters({
                  key: "selectedQuestionTypes",
                  value: params,
                });
              }}
              showAllOption={true}
              showSelectedOptionsOnTop={false}
            >
              <FilterLabelButton
                dsVersion="2.0"
                _legacy_variant="plain"
                variant="neutral"
                type="plain"
                label={t("worksheet:question_type")}
                labelValue={getLabelValueForMultiFilterLabelButton({
                  options: questionTypeOptions,
                  selectedOptions: selectedQuestionTypes,
                  allText: t("common:all"),
                  noneText: t("common:all"),
                })}
              />
            </ChecklistDropdown>
          ) : null}
          {/* Sub-topic */}
          {isToddleLibrary ? (
            <ConditionalWrapper
              type={"wrapperAsElement"}
              condition={disablePlannerElementsDropdown}
              wrapper={
                <Tooltip
                  tooltip={t("toddleLibrary:disabled_sub_topic_tooltip_text")}
                  placement={"top"}
                />
              }
            >
              <ChecklistDropdown
                options={subTopicOptions}
                value={selectedSubTopics}
                onChange={updateSubTopicFilter}
                showAllOption={true}
                showSelectedOptionsOnTop={false}
                isSearchEnabled={true}
                disabled={disablePlannerElementsDropdown}
              >
                <FilterLabelButton
                  dsVersion="2.0"
                  _legacy_variant="plain"
                  variant="neutral"
                  type="plain"
                  label={t("worksheet:sub_topic")}
                  labelValue={getLabelValueForMultiFilterLabelButton({
                    options: subTopicOptions,
                    selectedOptions: selectedSubTopics,
                    allText: t("common:all"),
                    noneText: t("common:all"),
                  })}
                />
              </ChecklistDropdown>
            </ConditionalWrapper>
          ) : null}
          {/* Topic */}
          <ConditionalWrapper
            type={"wrapperAsElement"}
            condition={disablePlannerElementsDropdown}
            wrapper={
              <Tooltip
                tooltip={t("toddleLibrary:disabled_topic_tooltip_text")}
                placement={"top"}
              />
            }
          >
            <ChecklistDropdown
              options={topicOptions}
              value={selectedTopics}
              onChange={updateTopicFilter}
              showAllOption={true}
              showSelectedOptionsOnTop={false}
              isSearchEnabled={true}
              disabled={disablePlannerElementsDropdown}
            >
              <FilterLabelButton
                dsVersion="2.0"
                _legacy_variant="plain"
                variant="neutral"
                type="plain"
                label={t("common:topic")}
                labelValue={getLabelValueForMultiFilterLabelButton({
                  options: topicOptions,
                  selectedOptions: selectedTopics,
                  allText: t("common:all"),
                  noneText: t("common:all"),
                })}
              />
            </ChecklistDropdown>
          </ConditionalWrapper>
        </div>
      ) : null}
      {/* Add random questions */}
      <div></div>
    </div>
  );
};

const mapStateToProps = state => {
  const curriculumType = getCurrentCurriculumProgramType(state);
  let globalGradeUid = getGlobalGradeUid();

  const filters = _.get(state, "worksheet.questionLibrary.filters", {});
  const libraryType = _.get(state, "worksheet.questionLibrary.libraryType", "");
  const isToddleLibraryPortal =
    state.login.activeTab === ACTIVE_TAB_TODDLE_LIBRARY;

  if (_.isEmpty(globalGradeUid) && isToddleLibraryPortal) {
    globalGradeUid = _.get(
      state,
      "toddleLibrary.globalFilter.grade.mappedOrganizationEntities[0].globalGrade.uid"
    );
  }
  return {
    filters,
    libraryType,
    isToddleLibraryPortal,
    globalGradeUid,
    curriculumType,
  };
};

const mapActionCreators = {
  updateQuestionLibraryFilters,
  updateQuestionLibraryType,
  resetQuestionLibraryState,
};

QuestionLibraryFilters.propTypes = {
  libraryType: PropTypes.string,
};

QuestionLibraryFilters.defaultProps = {
  subjectOptions: [],
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(QuestionLibraryFilters);
